import { Document } from '@contentful/rich-text-types';
import { isNewProductSegmentation } from 'api/product';
import IconAnimatedSpinner from 'assets/icons/animated-spinner.svg';
import cn from 'classnames';
import Badge from 'components/badge';
import Breadcrumbs from 'components/breadcrumbs';
import { useSegmentationContext } from 'contexts/segmentation';
import useExperiment from 'hooks/common/use-experiment';
import useMobile from 'hooks/common/use-mobile';
import { useBreadcrumbs } from 'hooks/product/use-breadcrumbs';
import { ShopifyImage } from 'interfaces/product';
import { useContentful } from 'modules/content-cms/components/context';
import BuyPanel from 'modules/product-details/components/buy-panel';
import ImagesGalleryDesktop from 'modules/product-details/components/images-gallery-desktop';
import ImagesGalleryMobile from 'modules/product-details/components/images-gallery-mobile';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gtm, scrollElement, selectElement } from 'tracking';
import FullScreenGallery from '../full-screen-gallery';
import JudgeMeReviewBadgeWrapper from '../judge-me-review-badge-wrapper';
import ProductTags from '../product-tags';
import SegmentBanner from '../segment-banner';
import Usps from '../usps';
import { useProductDetailsHeaderState } from './state';

const IncludedProducts = dynamic(() => import('../included-products'), {
  loading: () => (
    <IconAnimatedSpinner className="h-6 w-6 text-text-dark-bg-contrast-white" />
  ),
});

const AiBotTest = dynamic(() => import('../ai-bot-test'));

type FullScreenGalleryType = React.ElementRef<typeof FullScreenGallery>;

type ProductDetailsHeaderProps = {
  displayAsSection?: boolean;
  imageBackgroundColor?: string;
};

export interface ProductGallery {
  type: 'video' | 'image';
  videoData?: {
    videoUrl?: string;
    videoThumbnail?: string;
  };
  imageData?: ShopifyImage;
}

const ProductDetailsHeader: React.FC<ProductDetailsHeaderProps> = ({
  displayAsSection = false,
}) => {
  const { data: contentful } = useContentful();
  const router = useRouter();
  const isMobile = useMobile();
  const $gallery = useRef<FullScreenGalleryType>(null);
  const [selectedGalleryItemIndex, setSelectedGalleryItemIndex] = useState(0);
  // Since the first scroll happens when we sync the first image to show with the vairants,
  // we don't want to track that as a scroll event
  const firstRenderingTrackingRef = useRef(true);
  const firstOpenGalleryTrackingRef = useRef(true);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const {
    product,
    productSegmentation,
    selectedVariantId,
    includedProducts,
    isProductSegmentLoading,
  } = useProductDetailsHeaderState();
  const { lastSegmentation } = useSegmentationContext();

  const description = useMemo(() => {
    const usps = contentful.fields.header?.find(
      (headerEntry) => headerEntry.fields.productUsp
    );
    return usps ? (usps.fields.text as Document) : undefined;
  }, [contentful]);

  const { breadcrumbList, isLoading } = useBreadcrumbs({ product });

  const images = product.images;
  const svgLayerRef = useRef<number>();

  const onOpenGallery = (index: number): void => {
    gtm(
      selectElement({
        element: ['PDP Images', 'Open'],
      })
    );
    $gallery.current?.goToIndex(index);
    $gallery.current?.openGallery();
  };

  const productGallery = useMemo<ProductGallery[]>(() => {
    const galleryItems: ProductGallery[] = [];
    images.map((image) => {
      return galleryItems.push({
        type: 'image',
        imageData: image,
      });
    });

    return galleryItems;
  }, [images]);

  useEffect(() => {
    setIsGalleryOpen(false);
    if (!firstRenderingTrackingRef.current) {
      firstRenderingTrackingRef.current = true;
    }
    firstOpenGalleryTrackingRef.current = true;
  }, [router]);

  const ribbonText = useMemo(() => {
    try {
      return productSegmentation &&
        isNewProductSegmentation(productSegmentation)
        ? productSegmentation.recommendation_text
        : productSegmentation &&
            lastSegmentation &&
            productSegmentation.segmentation[lastSegmentation].content;
    } catch (error) {
      console.log({ error });
      return null;
    }
  }, [lastSegmentation, productSegmentation]);

  const uspTitle = useMemo(() => {
    if (productSegmentation && isNewProductSegmentation(productSegmentation)) {
      return productSegmentation.usp_title;
    }
    return null;
  }, [productSegmentation]);

  const tags: Array<string | { text: string; color: string }> = useMemo(
    () => [
      ...(product.metadata.accentuate.ribbonText1
        ? [
            {
              text: product.metadata.accentuate.ribbonText1,
              color: product.metadata.accentuate.ribbonColor,
            },
          ]
        : []),
    ],
    [
      product.metadata.accentuate.ribbonColor,
      product.metadata.accentuate.ribbonText1,
    ]
  );

  // FE :: AB Test - PDP AI Bot engagement test
  // @see https://petsdeli.atlassian.net/browse/PT-6961
  const { variant, loading } = useExperiment({
    baseEntryId: '1Li4be2nQvWINDQPCzzBsl',
  });

  const productInfo = useMemo(
    () => (
      <>
        <JudgeMeReviewBadgeWrapper product={product} />
        <h1 className="mb-3 align-middle  font-mindset text-2xl leading-[35.2px] text-text-primary lg:text-[32px]">
          {product.metadata.accentuate.mainTitle || product.title}
        </h1>
        {tags.length > 0 && (
          <div className="mb-5 mt-3 flex flex-row flex-wrap gap-2 ">
            {tags.map((v) => {
              if (typeof v === 'string') {
                return <ProductTags key={v} tags={[v]} />;
              } else {
                return (
                  <Badge key={v.text} type={v.color}>
                    {v.text}
                  </Badge>
                );
              }
            })}
          </div>
        )}
        <BuyPanel />
        {isMobile && !loading && variant === 1 && <AiBotTest />}
        {description && !isProductSegmentLoading && !displayAsSection && (
          <Usps defaultContent={description} uspTitle={uspTitle} />
        )}
      </>
    ),
    [
      displayAsSection,
      description,
      isMobile,
      isProductSegmentLoading,
      loading,
      product,
      tags,
      uspTitle,
      variant,
    ]
  );

  return (
    <>
      <FullScreenGallery
        firstOpenGalleryTrackingRef={firstOpenGalleryTrackingRef}
        product={product}
        productGallery={productGallery}
        ref={$gallery}
        selectedGalleryItemIndex={selectedGalleryItemIndex}
        setIsGalleryOpen={setIsGalleryOpen}
        setSelectedGalleryItemIndex={setSelectedGalleryItemIndex}
      />

      {/* Laptop PDP structure */}
      <div className="container hidden lg:block">
        <Breadcrumbs
          breadcrumbs={breadcrumbList}
          dataTest="breadcrumbs-pdp"
          isLoading={isLoading}
        />
        <div className="grid grid-cols-12 gap-5">
          <div data-test="image-section" className="col-span-7">
            <ImagesGalleryDesktop
              onOpenGallery={(index) => onOpenGallery(index)}
              onSwipe={(index) => {
                $gallery.current?.goToIndex(index);
              }}
              product={product}
              productGallery={productGallery}
              selectedGalleryItemIndex={selectedGalleryItemIndex}
              selectedVariantId={selectedVariantId}
              setSelectedGalleryItemIndex={setSelectedGalleryItemIndex}
              svgLayerRef={svgLayerRef}
              ribbonText={isProductSegmentLoading ? null : ribbonText}
            />
            {!isMobile && !loading && variant === 1 && <AiBotTest />}
          </div>
          <div className="col-span-5" data-test="pdp-title-block">
            {productInfo}
          </div>
        </div>
        {product && includedProducts && includedProducts.length > 0 && (
          <IncludedProducts
            product={product}
            includedProducts={includedProducts}
          />
        )}
      </div>

      {/*  mobile PDP structure */}
      <Breadcrumbs
        breadcrumbs={breadcrumbList}
        className="px-4 lg:hidden"
        dataTest="breadcrumbs-pdp"
        isLoading={isLoading}
      />

      <div className={cn('px-4 lg:hidden')}>
        <ImagesGalleryMobile
          onOpenGallery={(index) => onOpenGallery(index)}
          onSwipe={(index) => {
            if (isGalleryOpen) {
              if (!firstOpenGalleryTrackingRef.current) {
                gtm(
                  scrollElement({
                    element: ['PDP Images'],
                  })
                );
              } else {
                firstOpenGalleryTrackingRef.current = false;
              }
            } else {
              if (!firstRenderingTrackingRef.current) {
                gtm(
                  scrollElement({
                    element: ['PDP Images'],
                  })
                );
              } else {
                firstRenderingTrackingRef.current = false;
              }
            }
            $gallery.current?.goToIndex(index);
            setSelectedGalleryItemIndex(index);
          }}
          productGallery={productGallery}
          selectedGalleryItemIndex={selectedGalleryItemIndex}
          selectedVariantId={selectedVariantId}
          setSelectedGalleryItemIndex={setSelectedGalleryItemIndex}
          svgLayerIndex={svgLayerRef.current}
          svgLayerRef={svgLayerRef}
          variants={product.variants}
        />
        {!isProductSegmentLoading && ribbonText && (
          <SegmentBanner className="mt-2">{ribbonText}</SegmentBanner>
        )}

        <div className="-mx-4 rounded-t-[24px] bg-other-white px-4 pt-2">
          {productInfo}
        </div>
        {product && includedProducts && includedProducts.length > 0 && (
          <IncludedProducts
            product={product}
            includedProducts={includedProducts}
          />
        )}
      </div>
    </>
  );
};

export default ProductDetailsHeader;
